import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  Link,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import UndoIcon from '@mui/icons-material/Undo';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { ReservationPaymentDto } from '../../Types/reservationPaymentTypes';
import { BankDocumentsRoutes, CashDocumentsRoutes } from '../../../../Router/path';

interface PaymentHistoryTableProps {
  payments: ReservationPaymentDto[];
  isLoading: boolean;
}

const buildCashDocumentUrl = (cashDocumentId: number): string =>
  CashDocumentsRoutes.details.replace(':id', String(cashDocumentId));

const buildBankDocumentEntryUrl = (bankDocumentId: number, bankDocumentEntryId: number): string =>
  `${BankDocumentsRoutes.details.replace(':bankDocumentId', String(bankDocumentId))}#bank-document-entry-row-${bankDocumentEntryId}`;

const SOURCE_LABELS: Record<NonNullable<ReservationPaymentDto['source']>, string> = {
  reservation: 'Rezerwacja',
  prepaymentInvoice: 'FV zaliczkowa',
  finalInvoice: 'FV końcowa',
  standardInvoice: 'FV standard',
};

const renderSourceChip = (source?: ReservationPaymentDto['source']): React.ReactNode => {
  if (!source) return null;
  return <Chip label={SOURCE_LABELS[source]} size="small" />;
};

const renderAccountingLink = (payment: ReservationPaymentDto): React.ReactNode => {
  if (payment.cashDocumentId) {
    return (
      <Link component={RouterLink} to={buildCashDocumentUrl(payment.cashDocumentId)} underline="hover">
        KP/KW #{payment.cashDocumentId}
      </Link>
    );
  }

  if (payment.bankDocumentId && payment.bankDocumentEntryId) {
    return (
      <Link
        component={RouterLink}
        to={buildBankDocumentEntryUrl(payment.bankDocumentId, payment.bankDocumentEntryId)}
        underline="hover"
      >
        WB #{payment.bankDocumentId} / poz. {payment.bankDocumentEntryId}
      </Link>
    );
  }

  return '—';
};

const PaymentHistoryTable: React.FC<PaymentHistoryTableProps> = ({ payments, isLoading }) => {
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Ładowanie historii płatności...
        </Typography>
      </Box>
    );
  }

  if (payments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Brak historii płatności dla tej rezerwacji.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="60px">Typ</TableCell>
            <TableCell width="120px">Data</TableCell>
            <TableCell width="120px" align="right">
              Kwota
            </TableCell>
            <TableCell width="150px">Metoda</TableCell>
            <TableCell width="140px">Źródło</TableCell>
            <TableCell width="220px">Księgowanie</TableCell>
            <TableCell>Notatka</TableCell>
            <TableCell width="150px">Dodano</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.map((payment) => (
            <TableRow key={payment.id}>
              {/* Type Icon */}
              <TableCell>
                <Tooltip title={payment.paymentTypeLabel}>
                  {payment.amount >= 0 ? (
                    <PaymentIcon fontSize="small" color="success" />
                  ) : (
                    <UndoIcon fontSize="small" color="warning" />
                  )}
                </Tooltip>
              </TableCell>

              {/* Payment Date */}
              <TableCell>{dayjs(payment.paymentDate).format('DD.MM.YYYY')}</TableCell>

              {/* Amount */}
              <TableCell align="right">
                <Chip
                  label={`${payment.absoluteAmount.toFixed(2)} PLN`}
                  size="small"
                  color={payment.amount >= 0 ? 'success' : 'warning'}
                  variant="outlined"
                />
              </TableCell>

              {/* Payment Method */}
              <TableCell>{payment.paymentMethod}</TableCell>

              {/* Source */}
              <TableCell>{renderSourceChip(payment.source)}</TableCell>

              {/* Accounting Link */}
              <TableCell>{renderAccountingLink(payment)}</TableCell>

              {/* Note */}
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 200,
                  }}
                >
                  {payment.note || '—'}
                </Typography>
              </TableCell>

              {/* Created At */}
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(payment.createdAt).format('DD.MM.YYYY HH:mm')}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PaymentHistoryTable;
